import { ArrowsPointingOutIcon } from '@heroicons/react/24/solid';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ActivityRecord } from '~/store/activityRecords';
import { ReactComponent as Whatsapp } from '~/assets/svg/whatsapp.svg';
import { ReactComponent as Instagram } from '~/assets/svg/instagram.svg';
import { MessagingProducts } from '~/types';
import { motion } from 'motion/react';

const StatusBadge = ({ isEnded }: { isEnded: boolean }) => {
  const [t] = useTranslation('conversations');
  return (
    <motion.span
      className={`rounded-full px-2 py-1 text-xs font-semibold ${
        isEnded ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
      }`}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{
        opacity: 1,
        scale: 1,
        transition: {
          delay: 0.3,
          type: 'spring',
          stiffness: 400,
          damping: 20,
        },
      }}
      whileHover={{
        scale: 1.1,
        transition: { duration: 0.2 },
      }}
    >
      {isEnded ? t('ended') : t('active')}
    </motion.span>
  );
};

export const ActivityCard = ({
  activity,
  hasOpenConversation,
}: {
  activity: ActivityRecord;
  hasOpenConversation: boolean;
}) => {
  const [t] = useTranslation('conversations');
  const [searchParams, setSearchParams] = useSearchParams();

  const handleActivityClick = () => {
    // This sets the conversationId in the URL which is then used by the Messages component
    // to fetch and display the appropriate conversation
    if (searchParams.get('search')) {
      setSearchParams({
        conversationId: activity._id,
        hasOpenConversation: hasOpenConversation.toString(),
        search: searchParams.get('search') ?? '',
      });
    } else {
      setSearchParams({
        ...searchParams,
        conversationId: activity._id,
        hasOpenConversation: hasOpenConversation.toString(),
      });
    }
  };

  const IconComponent = {
    [MessagingProducts.WHATSAPP]: Whatsapp,
    [MessagingProducts.INSTAGRAM]: Instagram,
  };

  if (activity.isHidden === true) {
    return null;
  }

  const SelectedIcon =
    IconComponent[activity.messaging_type as keyof typeof IconComponent] ??
    Whatsapp;

  return (
    <motion.div
      className="flex gap-4 border-l pt-4"
      whileHover={{
        x: 4,
        transition: { duration: 0.2 },
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.8, rotateY: -20 }}
        animate={{
          opacity: 1,
          scale: 1,
          rotateY: 0,
          transition: {
            delay: 0.2,
            type: 'spring',
            stiffness: 300,
            damping: 20,
          },
        }}
        whileHover={{
          scale: 1.1,
          rotateY: 5,
          transition: { duration: 0.2 },
        }}
      >
        <SelectedIcon className="ml-[-12px] mt-2 h-6 w-6 text-current" />
      </motion.div>

      <motion.div
        className="border-custom-primary-accent-400 bg-custom-surface-200 w-full rounded-lg border p-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{
          opacity: 1,
          y: 0,
          transition: {
            delay: 0.1,
            type: 'spring',
            stiffness: 300,
            damping: 25,
          },
        }}
        whileHover={{
          y: -2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          borderColor: 'rgba(var(--primary), 0.5)',
          transition: { duration: 0.2 },
        }}
      >
        <div className="flex flex-row justify-between">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{
              opacity: 1,
              x: 0,
              transition: {
                delay: 0.2,
                type: 'spring',
                stiffness: 300,
                damping: 20,
              },
            }}
          >
            <motion.h3 className="text-md font-bold" whileHover={{ x: 2 }}>
              {t('conversation')}
            </motion.h3>
            <div className="flex flex-row gap-2 text-sm">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  transition: { delay: 0.4 },
                }}
              >
                {moment(activity.created_at).format('lll')}
              </motion.p>
            </div>
          </motion.div>

          <motion.div
            className="flex flex-row items-center gap-2"
            initial={{ opacity: 0, x: 10 }}
            animate={{
              opacity: 1,
              x: 0,
              transition: {
                delay: 0.25,
                type: 'spring',
                stiffness: 300,
                damping: 20,
              },
            }}
          >
            <StatusBadge isEnded={!!activity.ended_at} />
            <motion.div
              whileHover={{
                scale: 1.2,
                rotate: 5,
                transition: { duration: 0.2 },
              }}
              whileTap={{
                scale: 0.9,
                rotate: -5,
                transition: { duration: 0.1 },
              }}
            >
              <ArrowsPointingOutIcon
                className="h-6 w-6 cursor-pointer"
                onClick={handleActivityClick}
              />
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};
